import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import type { Shift } from 'modules/shift_management/lib/types'
import shiftsCollection from 'modules/shift_management/store/api/collections/shifts'

import { trackDeleteShiftsEvent } from '../lib/trackDeleteEvent'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface useDestroyShiftsProps {
  shifts: Shift[]
  employeeCount: number
  destroyBackUpShifts: boolean
}

const useDestroyShifts = ({
  shifts,
  employeeCount,
  destroyBackUpShifts,
}: useDestroyShiftsProps) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)
  const shiftIds = uniq(shifts.map((shift) => shift.id))

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackDeleteShiftsEvent({
        shifts,
        view,
        employeeCount,
        shiftsConflicts,
        from: 'top_bar_button',
      })

      await shiftsCollection.rpc('bulk_destroy', {
        ids: shiftIds,
        destroy_backup_shifts: destroyBackUpShifts,
      })

      // Get shiftsRows where there are affected shifts to update stats
      const shiftKeys = new Set(uniq(shifts.map((shift) => shift.rowKeyReference(view))))
      const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
        return shiftKeys.has(row.key)
      })

      await refreshStats(previousShiftsRows)

      const shiftsToRefresh = destroyBackUpShifts
        ? shifts
        : shifts.filter((shift) => !shift.isBackup)
      dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts: shiftsToRefresh, view })
      dispatchSelection({ type: 'CLEAR_SELECTION' })

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.delete_shifts_success'),
    }
  )

  return {
    onDestroy: performAction,
    submitting,
  }
}

const useDestroyShift = ({ shift, isBackup }: { shift: Shift; isBackup?: boolean }) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)
  const shiftIds = [shift.id]

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackDeleteShiftsEvent({
        shifts: [shift],
        view,
        employeeCount: 1,
        shiftsConflicts,
        from: 'contextual_button',
        isBackup,
      })

      await shiftsCollection.rpc('bulk_destroy', { ids: shiftIds, destroy_backup_shifts: true })
      await refreshStats(shiftsRows)

      dispatchShiftsRows({ type: 'DESTROY_SHIFT', shift, view })

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.delete_shift_modal.success'),
    }
  )

  return {
    onDestroy: performAction,
    submitting,
  }
}

export { useDestroyShift, useDestroyShifts }
